/**
 * Market Manager
 *
 * Manages markets where players SELL fish and access quest boards.
 * Follows the Manager singleton pattern from architecture.
 */

import { useGameStore } from '@/state';
import { eventBus } from '@/services/EventBus';
import { MarketDefinition, QuestDefinition, GameEvent, FishStack } from '@/types';
import { questBoardManager } from '../quests/QuestBoardManager';
import { evaluateQuestUnlock } from '@/utils';
import { customQuestManager } from '../quests/CustomQuestManager';
import { ZONE_DEFINITIONS } from '@/data';

export class MarketManager {
  private markets: Map<string, MarketDefinition> = new Map();
  private questDefinitions: Map<string, QuestDefinition> = new Map();
  private refreshIntervalId: number | null = null;

  /**
   * Register a market definition
   */
  registerMarket(definition: MarketDefinition): void {
    this.markets.set(definition.id, definition);
  }

  /**
   * Register multiple markets
   */
  registerMarkets(definitions: MarketDefinition[]): void {
    definitions.forEach((def) => this.registerMarket(def));
  }

  /**
   * Get a market definition by ID
   */
  getMarket(marketId: string): MarketDefinition | undefined {
    return this.markets.get(marketId);
  }

  /**
   * Get all registered markets
   */
  getAllMarkets(): MarketDefinition[] {
    return Array.from(this.markets.values());
  }

  /**
   * Get markets available in a specific zone
   */
  getMarketsInZone(_zoneId: string): MarketDefinition[] {
    // Would need zone definitions to check which markets are in zone
    // For now, return all markets
    return this.getAllMarkets();
  }

  /**
   * Check if player has unlocked a market
   */
  isMarketUnlocked(marketId: string): boolean {
    const state = useGameStore.getState();
    return state.playerState.shops.unlockedMarkets.includes(marketId);
  }

  /**
   * Check if player meets unlock requirements for a market
   */
  canUnlockMarket(marketId: string): boolean {
    const market = this.getMarket(marketId);
    if (!market) return false;

    if (market.unlockedByDefault) return true;
    if (this.isMarketUnlocked(marketId)) return true;

    const state = useGameStore.getState();
    const requirements = market.unlockRequirements;

    if (!requirements) return true;

    // Check level requirement
    if (requirements.minLevel !== undefined) {
      // Future: Add player level system
      // if (state.playerState.level < requirements.minLevel) return false;
    }

    // Check currency requirement
    if (requirements.minCurrency !== undefined) {
      if (state.playerState.currency < requirements.minCurrency) return false;
    }

    // Check completed quests
    if (requirements.completedQuests) {
      const completed = state.playerState.quests.completedQuests;
      if (!requirements.completedQuests.every((q) => completed.includes(q))) {
        return false;
      }
    }

    // Check fish caught milestone
    if (requirements.totalFishCaught !== undefined) {
      if (state.playerState.stats.totalFishCaught < requirements.totalFishCaught) {
        return false;
      }
    }

    // Check fish sold milestone
    if (requirements.totalFishSold !== undefined) {
      if (state.playerState.stats.totalFishSold < requirements.totalFishSold) {
        return false;
      }
    }

    // Check buddy count
    if (requirements.buddyCount !== undefined) {
      const hiredBuddies = state.playerState.fishingBuddies.filter((b) => b.hired).length;
      if (hiredBuddies < requirements.buddyCount) return false;
    }

    // Check zone unlocked
    if (requirements.zoneUnlocked) {
      if (!state.playerState.unlockedZones.includes(requirements.zoneUnlocked)) {
        return false;
      }
    }

    return true;
  }

  /**
   * Unlock a market
   */
  unlockMarket(marketId: string): boolean {
    if (!this.canUnlockMarket(marketId)) {
      console.warn(`Cannot unlock market ${marketId} - requirements not met`);
      return false;
    }

    if (this.isMarketUnlocked(marketId)) {
      console.warn(`Market ${marketId} already unlocked`);
      return false;
    }

    useGameStore.setState((draft) => {
      draft.playerState.shops.unlockedMarkets.push(marketId);
    });

    eventBus.emit(GameEvent.SHOP_UNLOCKED, {
      shopId: marketId,
      shopType: 'market',
    });

    return true;
  }

  /**
   * Calculate selling price for fish at a market
   */
  calculateFishPrice(
    marketId: string,
    fishStack: FishStack,
    basePricePerFish: number
  ): number {
    const market = this.getMarket(marketId);
    if (!market) return 0;

    // Apply market price multiplier
    const pricePerFish = basePricePerFish * market.fishPriceMultiplier;
    const totalPrice = pricePerFish * fishStack.count;

    return Math.floor(totalPrice);
  }

  /**
   * Check if a market accepts a specific fish type
   */
  acceptsFish(marketId: string, fishStack: FishStack): boolean {
    const market = this.getMarket(marketId);
    if (!market) return false;

    // Check accepted qualities
    if (market.acceptedQualities && market.acceptedQualities.length > 0) {
      if (!market.acceptedQualities.includes(fishStack.quality)) {
        return false;
      }
    }

    // Check accepted species
    if (market.acceptedSpecies && market.acceptedSpecies.length > 0) {
      if (!market.acceptedSpecies.includes(fishStack.definitionId)) {
        return false;
      }
    }

    return true;
  }

  /**
   * Visit a market (update last visited timestamp)
   */
  visitMarket(marketId: string): void {
    useGameStore.setState((draft) => {
      draft.playerState.shops.visitedShops[marketId] = Date.now();
    });
  }

  /**
   * Register a quest definition (for quest boards)
   */
  registerQuestDefinition(definition: QuestDefinition): void {
    this.questDefinitions.set(definition.id, definition);
  }

  /**
   * Register multiple quest definitions
   */
  registerQuestDefinitions(definitions: QuestDefinition[]): void {
    definitions.forEach((def) => this.registerQuestDefinition(def));
  }

  /**
   * Load and register custom quests from CustomQuestManager
   */
  loadCustomQuests(): void {
    const customQuests = customQuestManager.getAllCustomQuests();
    customQuests.forEach((quest) => {
      this.registerQuestDefinition(quest);
    });
    console.log(`[MarketManager] Loaded ${customQuests.length} custom quests`);
  }

  /**
   * Reload all quests (default + custom)
   * Useful after editing quests in the quest tool
   */
  reloadAllQuests(): void {
    // Keep default quests, reload custom quests

    // Mark which quests are custom (will be replaced)
    const customQuestIds = new Set(
      customQuestManager.getAllCustomQuests().map((q) => q.id)
    );

    // Remove custom quests from the registry
    customQuestIds.forEach((id) => {
      this.questDefinitions.delete(id);
    });

    // Reload custom quests
    this.loadCustomQuests();
  }

  /**
   * Get a quest definition by ID
   */
  getQuestDefinition(questId: string): QuestDefinition | undefined {
    return this.questDefinitions.get(questId);
  }

  /**
   * Get all quest definitions (default + custom)
   */
  getAllQuestDefinitions(): QuestDefinition[] {
    return Array.from(this.questDefinitions.values());
  }

  /**
   * Get fish species available in a market's zone
   */
  private getZoneFishPool(marketId: string): string[] {
    // Find the zone that contains this market
    const zone = ZONE_DEFINITIONS.find((z) => z.markets.includes(marketId));
    if (zone) {
      return zone.fishPool;
    }
    // Fallback
    return ['brook_trout', 'brown_trout', 'cutthroat_trout'];
  }

  /**
   * Get all available quests for a market's quest board
   * Respects quest unlock conditions and progression gates.
   * Procedural quests are cached and refresh every 10 minutes (configurable).
   */
  getAvailableQuests(marketId: string): QuestDefinition[] {
    const market = this.getMarket(marketId);
    if (!market) return [];

    const state = useGameStore.getState();
    const questBoard = market.questBoard;
    const availableQuests: QuestDefinition[] = [];

    // Build lookup sets for active and completed quests
    const activeQuestIds = new Set(
      state.playerState.quests.activeQuests.map((q) => q.questId)
    );
    const completedQuestIds = new Set(state.playerState.quests.completedQuests);

    // Get curated quests (tutorial/story quests)
    // Only show quests that are not active, not completed, and meet unlock requirements
    for (const questId of questBoard.curatedQuestPool) {
      const quest = this.getQuestDefinition(questId);
      if (quest) {
        const isActive = activeQuestIds.has(questId);
        const isCompleted = completedQuestIds.has(questId);
        const meetsUnlockRequirements = evaluateQuestUnlock(quest, state.playerState);

        if (!isActive && !isCompleted && meetsUnlockRequirements) {
          availableQuests.push(quest);
        }
      }
    }
    const activeProceduralCount = state.playerState.quests.activeQuests.filter(
      (q) => q.expiresAt !== undefined
    ).length;
    const proceduralCapReached = activeProceduralCount >= questBoardManager.getMaxProceduralQuests();

    // Get procedural quests from cache (generates if needed)
    const zoneFishPool = this.getZoneFishPool(marketId);
    const proceduralQuests = questBoardManager.getCachedQuests(
      marketId,
      questBoard.proceduralQuestPool,
      zoneFishPool
    );

    // Register procedural quest definitions so QuestManager can track progress
    for (const quest of proceduralQuests) {
      if (!this.questDefinitions.has(quest.id)) {
        this.questDefinitions.set(quest.id, quest);
      }
    }

    // Show procedural quests only if under the active cap and meeting unlock conditions
    if (!proceduralCapReached) {
      for (const quest of proceduralQuests) {
        if (!activeQuestIds.has(quest.id) && !completedQuestIds.has(quest.id) && evaluateQuestUnlock(quest, state.playerState)) {
          availableQuests.push(quest);
        }
      }
    }

    // Get buddy quests (only if player has hired at least one hauler)
    const hasHiredHauler = state.playerState.haulerBuddies.some((h) => h.hired);

    if (hasHiredHauler && questBoard.buddyQuestPool && questBoard.buddyQuestPool.length > 0) {
      const buddyQuests = questBoardManager.getCachedBuddyQuests(
        marketId,
        questBoard.buddyQuestPool,
        zoneFishPool
      );

      // Register buddy quest definitions so QuestManager can track progress
      for (const quest of buddyQuests) {
        if (!this.questDefinitions.has(quest.id)) {
          this.questDefinitions.set(quest.id, quest);
        }
      }

      // Filter out accepted/completed buddy quests and check unlock conditions
      for (const quest of buddyQuests) {
        if (!activeQuestIds.has(quest.id) && !completedQuestIds.has(quest.id) && evaluateQuestUnlock(quest, state.playerState)) {
          availableQuests.push(quest);
        }
      }
    }

    return availableQuests;
  }

  /**
   * Check if quest board needs refresh
   */
  shouldRefreshQuestBoard(marketId: string): boolean {
    const market = this.getMarket(marketId);
    if (!market) return false;

    const questBoard = market.questBoard;
    const lastRefresh = questBoard.lastRefreshDate || 0;
    const refreshIntervalMs = questBoard.refreshInterval * 60 * 60 * 1000; // Convert hours to ms
    const now = Date.now();

    return now - lastRefresh >= refreshIntervalMs;
  }

  /**
   * Refresh a market's quest board
   */
  refreshQuestBoard(marketId: string): void {
    const market = this.getMarket(marketId);
    if (!market) return;

    if (!this.shouldRefreshQuestBoard(marketId)) {
      return;
    }

    // Update last refresh date
    market.questBoard.lastRefreshDate = Date.now();

    // Emit refresh event
    eventBus.emit(GameEvent.QUEST_BOARD_REFRESHED, {
      marketId,
      timestamp: Date.now(),
    });

    console.log(`[MarketManager] Quest board refreshed for market ${marketId}`);
  }

  /**
   * Check for quest board refreshes across all markets
   */
  checkQuestBoardRefreshes(): void {
    this.getAllMarkets().forEach((market) => {
      this.refreshQuestBoard(market.id);
    });
  }

  /**
   * Initialize manager
   */
  initialize(): void {
    // Set up periodic quest board refresh checks
    this.refreshIntervalId = window.setInterval(() => {
      this.checkQuestBoardRefreshes();
    }, 60000); // Check every minute

    console.log('[MarketManager] Initialized');
  }

  /**
   * Cleanup
   */
  cleanup(): void {
    // Clear intervals
    if (this.refreshIntervalId !== null) {
      window.clearInterval(this.refreshIntervalId);
      this.refreshIntervalId = null;
    }
  }
}

// Singleton instance
export const marketManager = new MarketManager();
